import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { useEmail } from '../../hooks/useEmail';
import { resolveAttachmentPreview } from '../../utils/attachmentPreview';

/**
 * Shows a mail attachment before it ever reaches the disk: the file is fetched
 * as a Blob and rendered inside the dialog, with an explicit download button as
 * the second step. Types outside the preview allowlist are download-only.
 */
const AttachmentPreviewModal = ({ open, onClose, mailId, attachmentId, fileName, contentType }) => {
  const { downloadMailAttachment } = useEmail();
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || !mailId || !attachmentId) return undefined;

    let cancelled = false;
    let objectUrl = null;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const blob = await downloadMailAttachment(mailId, attachmentId);
        const preview = resolveAttachmentPreview({
          contentType: contentType || blob.type,
          fileName,
          size: blob.size,
        });

        let text = null;
        if (preview.kind === 'text') {
          text = await blob.text();
        } else if (preview.kind !== 'none') {
          // Re-type the blob so the browser can only interpret it as the kind
          // the allowlist settled on, never as whatever the mail declared.
          objectUrl = window.URL.createObjectURL(new Blob([blob], { type: preview.mimeType }));
        }

        if (cancelled) {
          if (objectUrl) window.URL.revokeObjectURL(objectUrl);
          return;
        }
        setLoaded({ blob, url: objectUrl, text, preview });
      } catch (error) {
        console.error('Error loading attachment:', error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mailId, attachmentId, contentType, fileName]);

  // Drop the loaded file when the dialog closes so the next attachment starts clean.
  useEffect(() => {
    if (open) return;
    setLoaded(null);
    setFailed(false);
  }, [open]);

  const handleDownload = useCallback(() => {
    if (!loaded) return;
    try {
      const url = window.URL.createObjectURL(loaded.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Fehler beim Herunterladen des Anhangs');
    }
  }, [loaded, fileName]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ wordBreak: 'break-all' }}>
        {fileName}
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Vorschau des Anhangs — heruntergeladen wird erst über den Button unten.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && failed && <Alert severity='error'>Der Anhang konnte nicht geladen werden.</Alert>}

        {!loading && !failed && loaded?.preview.kind === 'image' && loaded.url && (
          <Box
            component='img'
            src={loaded.url}
            alt={fileName}
            sx={{ display: 'block', mx: 'auto', maxWidth: '100%', maxHeight: '60vh' }}
          />
        )}

        {!loading && !failed && loaded?.preview.kind === 'pdf' && loaded.url && (
          <Box
            component='iframe'
            src={loaded.url}
            title={fileName}
            sx={{ width: '100%', height: '60vh', border: 0 }}
          />
        )}

        {!loading && !failed && loaded?.preview.kind === 'text' && (
          <Box
            component='pre'
            sx={{
              m: 0,
              maxHeight: '60vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '0.8rem',
            }}
          >
            {loaded.text}
          </Box>
        )}

        {!loading && !failed && loaded?.preview.kind === 'none' && (
          <Alert severity='info'>
            Für diesen Dateityp gibt es keine Vorschau. Die Datei lässt sich herunterladen — bei
            einer verdächtigen Mail besser nicht.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
        <Button
          variant='contained'
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!loaded}
        >
          Herunterladen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachmentPreviewModal;
